import { IDeviceSize } from '@checkout/device';
import { IItem } from '@checkout/items';
import * as cx from 'classnames';
import * as React from 'react';
import { Fragment } from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { CheckoutContextConfig } from '../../../apps/CheckoutApp';
import messages from '../../../i18n/base-en';
import { addLineItemRequest, removeLineItemRequest } from '../../../reducers/checkout';
import { lg, lsm, md, sm, xs } from '../../../utils/utils';
import { Button, IconButton, Price, ProductDetails, ProductImage, ProductName, Spinner } from '../../presentational';
import * as Styles from './ProductItem.scss';

interface IOwnProps extends InjectedIntlProps {
  item: IItem;
  increaseQuantity: typeof addLineItemRequest;
  decreaseQuantity: typeof removeLineItemRequest;
  readOnly?: boolean;
  isGiftWrapProduct?: boolean;
  urlPrefix?: string;
  isFetching?: boolean;
  isLoadMoreVisible?: boolean;
  loadMoreButtonLabel?: string;
  onLoadMoreItems?: (event: React.SyntheticEvent<HTMLButtonElement>) => void;
  withThumbnails?: boolean;
}

type Props = IOwnProps;

interface IOwnState {
  increaseCount: number;
  decreaseCount: number;
  isProductDetailsVisible: boolean;
  deviceSize: IDeviceSize;
  isFetching: boolean;
}

class ProductItem extends React.Component<Props, IOwnState> {

  state: IOwnState = {
    increaseCount: 0,
    decreaseCount: 0,
    isProductDetailsVisible: false,
    deviceSize: {
      xs: true,
      sm: false,
      lsm: false,
      md: false,
      lg: false,
    },
    isFetching: false
  };

  componentDidMount() {
    this.handleCheckDeviceSize();
  }

  componentWillMount() {
    window.addEventListener('resize', this.handleCheckDeviceSize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleCheckDeviceSize);
  }

  handleCheckDeviceSize = () => {
    this.setState({
      deviceSize: {
        xs: xs(),
        sm: sm(),
        lsm: lsm(),
        md: md(),
        lg: lg(),
      }
    });
  }

  componentWillReceiveProps(nextProps: Props) {
    this.setState({
      increaseCount: 0,
      decreaseCount: 0
    });

    if (nextProps && nextProps.isFetching !== undefined && nextProps.isFetching !== this.state.isFetching && this.state.isFetching) {
      this.setState({
        isFetching: nextProps.isFetching
      });
    }
  }

  increase = () => {
    let { increaseCount } = this.state;
    const { increaseQuantity: increase, item } = this.props;

    this.setState({
      increaseCount: ++increaseCount,
      isFetching: true
    });
    increase({ line: item.line, quantity: increaseCount });
  }

  decrease = () => {
    let { decreaseCount } = this.state;
    const { decreaseQuantity: decrease, item } = this.props;

    if (item.quantity >= decreaseCount) {
      this.setState({
        decreaseCount: ++decreaseCount,
        isFetching: true
      });
      decrease({ line: item.line, quantity: decreaseCount });
    }
  }

  remove = () => {
    const { decreaseQuantity: removeProduct, item } = this.props;
    this.setState({
      isFetching: true
    });
    removeProduct({ line: item.line, quantity: item.quantity });
  }

  toggleProductDetails = () => {
    const { isProductDetailsVisible } = this.state;

    this.setState({
      isProductDetailsVisible: !isProductDetailsVisible,
    });
  }

  render() {
    const { readOnly, item, isGiftWrapProduct, urlPrefix, isLoadMoreVisible, onLoadMoreItems, loadMoreButtonLabel, intl, withThumbnails } = this.props;
    const { formatMessage } = intl;
    const { isFetching } = this.state;
    const { isProductDetailsVisible, deviceSize } = this.state;
    const { product, quantity, line, priceEach, totalPrice, anyDiscount, priceEachBeforeDiscount, sku, size } = item;
    let uri = product && product.categoryUri ? `/${ product.categoryUri }/${ product.uri }` : `/${ product && product.uri }`;
    const showThumbnails = !readOnly || withThumbnails;

    // Use url prefix if defined in contextCheckoutConfig.ts
    if (urlPrefix) {
      uri = `/${ urlPrefix }${ uri }`;
    }

    const itemClassNames = cx({
      [Styles.item]: true,
      [Styles.giftWrap]: isGiftWrapProduct,
    });

    const innerClassNames = cx({
      [Styles.inner]: true,
      [Styles.faded]: isLoadMoreVisible,
    });

    const spinnerClassNames = cx({
      [Styles.spinner]: true,
      [Styles.spinnerVisible]: isFetching,
    });

    const priceClassNames = cx({
      [Styles.priceHided]: isFetching,
    });

    return (
      <CheckoutContextConfig.Consumer>
        {(checkoutContextConfig => (
          <Fragment>
            <div className={itemClassNames} key={ line}>
              {isLoadMoreVisible && (
                <div className={Styles.moreButton}>
                  <Button
                    low={true}
                    width="short"
                    align="center"
                    type="submit"
                    variant="primary"
                    onClick={onLoadMoreItems}
                    label={loadMoreButtonLabel}
                  />
                </div>
              )}
              <div className={innerClassNames}>
                {showThumbnails && product && product.media && product.media.standard && (
                  <div className={Styles.image}>
                    <ProductImage
                      onClick={checkoutContextConfig.ui.productItem.withProductDetails && !readOnly && deviceSize.md ?
                        { action: this.toggleProductDetails } :
                        { uri }}
                      src={product.media.standard[checkoutContextConfig.ui.productItem.indexImageAsCover] || product.media.standard[0]}
                      alt={product.name}
                      isClickable={!isGiftWrapProduct}
                    />
                  </div>
                )}
                <div className={Styles.details}>
                  <div className={Styles.left}>
                    {product && (
                      <ProductName
                        onClick={checkoutContextConfig.ui.productItem.withProductDetails && !readOnly && deviceSize.md ?
                          { action: this.toggleProductDetails } :
                          { uri }}
                        name={product.name}
                        isClickable={!isGiftWrapProduct}
                      />
                    )}
                    <div className={Styles.attributes}>
                      {checkoutContextConfig.ui.productItem.isSkuVisible && !isGiftWrapProduct && <div className={Styles.line}>{sku}</div>}
                      {product && product.variantName && checkoutContextConfig.ui.productItem.isVariantNameVisible &&
                        !isGiftWrapProduct && (
                          <div className={Styles.line}>{product.variantName}</div>
                        )}
                      {checkoutContextConfig.ui.productItem.isSizeVisible &&
                        !isGiftWrapProduct && <div className={Styles.line}>{size}</div>}
                    </div>
                    {!(checkoutContextConfig.ui.productItem.withProductDetails && !readOnly && deviceSize.md) && !readOnly && !isGiftWrapProduct && (
                      <div className={Styles.buttons}>
                        <div className={Styles.quantity}>
                          <IconButton onClick={this.decrease} icon="minus" />
                          <span className={Styles.value}>{quantity}</span>
                          <IconButton onClick={this.increase} icon="plus" />
                        </div>
                        <div className={Styles.remove}>
                          <button type="button" className={Styles.removeButton} onClick={this.remove}>
                            {formatMessage(messages.remove_button_label)}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={Styles.right}>
                    <div className={Styles.price}>
                      <div className={spinnerClassNames}><Spinner /></div>
                      <div className={priceClassNames}>
                        <Price
                          quantity={quantity}
                          anyDiscount={anyDiscount}
                          priceEach={priceEach}
                          totalPrice={totalPrice}
                          priceEachBeforeDiscount={priceEachBeforeDiscount}
                        />
                      </div>
                    </div>
                    {product && product.klarna_age_limit && product.klarna_age_limit === '18' && (
                      <span className={Styles.ageLimitLabel}>18 År</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {checkoutContextConfig.ui.productItem.withProductDetails && isProductDetailsVisible && !readOnly && (
              <ProductDetails
                close={this.toggleProductDetails}
                increase={this.increase}
                decrease={this.decrease}
                removeProduct={this.remove}
                quantity={quantity}
                item={item}
              />
            )}
          </Fragment>
        ))}
      </CheckoutContextConfig.Consumer>
    );
  }
}

export default injectIntl(ProductItem);
